import { computed } from 'vue';
import {
    useReferenceSelectionStore,
    type ReferenceTag,
    type TagColor,
} from '../stores/referenceSelectionStore';

/** Minimal row shape the references tables iterate over. */
interface SelectableRow {
    content_hash?: string;
}

/**
 * Bulk-selection helpers for a references table.
 *
 * Each table view (CDM shared view + the legacy EmailCommunication / EDoc
 * views) calls this in setup() with a getter for the row array it renders,
 * and only adds markup — a checkbox column wired to these helpers and the
 * tag pills/indicator in the primary cell. Selection and tag state live in
 * the referenceSelection store (see referenceSelectionStore.ts).
 */
export function useRowSelection(getRows: () => SelectableRow[]) {
    const selectionStore = useReferenceSelectionStore();

    const allChecked = computed(() => {
        const rows = getRows();
        return (
            rows.length > 0 && rows.every((r) => selectionStore.isSelected(r.content_hash))
        );
    });

    const someChecked = computed(
        () =>
            !allChecked.value &&
            getRows().some((r) => selectionStore.isSelected(r.content_hash)),
    );

    const toggleAll = () => {
        const hashes = getRows().map((r) => r.content_hash);
        if (allChecked.value) {
            selectionStore.deselectAll(hashes);
        } else {
            selectionStore.selectAll(hashes);
        }
    };

    const isChecked = (row: SelectableRow) => selectionStore.isSelected(row.content_hash);
    const toggleChecked = (row: SelectableRow) => selectionStore.toggleRow(row.content_hash);

    const rowTags = (row: SelectableRow): ReferenceTag[] =>
        selectionStore.tagsForRow(row.content_hash);
    const tagColor = (tag: ReferenceTag): TagColor => selectionStore.colorForTag(tag);

    return {
        allChecked,
        someChecked,
        toggleAll,
        isChecked,
        toggleChecked,
        rowTags,
        tagColor,
    };
}
